import React, { useEffect, useRef, useState } from "react";
import "../styles/GalleryStyles.css";
import Logger from "../../../utils/Logger";
import { useApi } from "../../../service/ApiContext";
import { LocalStorage } from "../../../utils/Utils";
import { Media } from "reactstrap";
import axios from "axios";
import { useAlert } from "../../../components/AlertContextComponent";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

interface VideoRecorderProps {
  mediaType: string;
  onSubmit: (file: File) => void | Promise<void>;
  disabled?: boolean;
  program_id?: string;
  subject_id?: string;
  topic_id?: string;
  video_id?: string;
  program_name?: string;
  video_name?: string;
  subject_name?: string;
  topic_name?: string;
}

type ResolutionType = "VGA (640x480)" | "HD (1280x720)" | "FHD (1920x1080)";
const MAX_DURATION = 30; // 30 seconds limit

const VideoRecorder: React.FC<VideoRecorderProps> = ({
  onSubmit,
  mediaType,
  disabled = false,
  program_id,
  subject_id,
  topic_id,
  video_id,
  program_name,
  video_name,
  subject_name,
  topic_name,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Refs to hold current values for unmount cleanup
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvasStreamRef = useRef<MediaStream | null>(null);
  const drawAnimationRef = useRef<number | null>(null);
  const activeStreamRef = useRef<MediaStream | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [permissionGranted, setPermissionGranted] = useState<boolean>(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [loadingCamera, setLoadingCamera] = useState<boolean>(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Recording states
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [seconds, setSeconds] = useState<number>(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const programs = useSelector(
    (state: RootState) => state.events.selectedValues,
  );
  const selectedProgramId = programs?.[0]?.program_id || "";
  const selectedProgramName = programs?.[0]?.program_title || "";
  // Custom Resolution & Audio states
  const [resolution, setResolution] = useState<ResolutionType>("HD (1280x720)");
  const [prepCountdown, setPrepCountdown] = useState<number | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileNameError, setFileNameError] = useState("");
  // Audio level meter states
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [audioLevel, setAudioLevel] = useState<number>(0); // 0 to 100

  //   // Parse width & height based on selection
  //   const getResolutionDimensions = (res: ResolutionType) => {
  //     switch (res) {
  //       case "VGA (640x480)":
  //         return { width: 640, height: 480 };
  //       case "FHD (1920x1080)":
  //         return { width: 1920, height: 1080 };
  //       case "HD (1280x720)":
  //       default:
  //         return { width: 1280, height: 720 };
  //     }
  //   };

  const startCamera = async (resType: ResolutionType = resolution) => {
    setLoadingCamera(true);
    setPermissionError(null);

    // Stop any existing tracks and clean up audio visualizer directly
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    setAudioLevel(0);

    // const { width, height } = getResolutionDimensions(resType);
    const width = 1280;
    const height = 720;
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: width },
          height: { ideal: height },
          facingMode: "user",
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      setStream(mediaStream);
      setPermissionGranted(true);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.muted = true; // Mute preview to avoid feedback loop
      }

      // Initialize real-time audio volume visualizer
      setupAudioMeter(mediaStream);
    } catch (error: any) {
      setPermissionGranted(false);
      setPermissionError(
        error.name === "NotAllowedError"
          ? "Permission denied. Jozuna requires camera & microphone permissions to record videos."
          : "Could not access media devices. Please check connections or permissions.",
      );
    } finally {
      setLoadingCamera(false);
    }
  };

  const setupAudioMeter = (mediaStream: MediaStream) => {
    try {
      // Clean up previous context
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }

      const AudioCtx =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;

      const audioTracks = mediaStream.getAudioTracks();
      if (audioTracks.length === 0) return;

      const audioCtx = new AudioCtx();
      const source = audioCtx.createMediaStreamSource(mediaStream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);

      audioContextRef.current = audioCtx;
      analyserRef.current = analyser;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const checkVolume = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);

        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const average = sum / bufferLength;
        const normalizedVol = Math.min(Math.round((average / 128) * 100), 100);
        setAudioLevel(normalizedVol);

        animationFrameRef.current = requestAnimationFrame(checkVolume);
      };

      checkVolume();
    } catch (e) {}
  };

  const stopCamera = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setAudioLevel(0);
  };

  //   const handleResolutionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
  //     const newRes = e.target.value as ResolutionType;
  //     setResolution(newRes);
  //     if (permissionGranted && !previewUrl) {
  //       startCamera(newRes);
  //     }
  //   };

  const getSupportedMimeType = () => {
    const mimeTypes = [
      //   "video/webm;codecs=vp9,opus",
      //   "video/webm;codecs=vp8,opus",
      "video/webm",
      //   "video/mp4", // Safari fallback
    ];
    for (const type of mimeTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return "";
  };

  const startPreparationCountdown = () => {
    if (!stream || prepCountdown !== null) return;

    setPrepCountdown(3);
    const interval = setInterval(() => {
      setPrepCountdown((prev) => {
        if (prev === null) {
          clearInterval(interval);
          return null;
        }
        if (prev <= 1) {
          clearInterval(interval);
          setTimeout(() => {
            executeStartRecording();
            setPrepCountdown(null);
          }, 600);
          return 0; // Action state
        }
        return prev - 1;
      });
    }, 1000);
  };

  const createCanvasStream = () => {
    if (!videoRef.current || !stream || !canvasRef.current) return null;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    if (!ctx) return null;

    const draw = () => {
      if (!videoRef.current) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Flip back before recording
      ctx.save();
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);

      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

      ctx.restore();

      drawAnimationRef.current = requestAnimationFrame(draw);
    };

    draw();

    const canvasStream = canvas.captureStream(30);

    // keep microphone
    stream.getAudioTracks().forEach((track) => {
      canvasStream.addTrack(track);
    });

    canvasStreamRef.current = canvasStream;

    return canvasStream;
  };

  const executeStartRecording = () => {
    if (!stream) return;

    chunksRef.current = [];
    const mimeType = getSupportedMimeType();

    try {
      const recordingStream = createCanvasStream();

      if (!recordingStream) return;

      const recorder = new MediaRecorder(recordingStream, {
        mimeType,
      });
      // const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        setRecordedBlob(blob);
        setPreviewUrl(url);
        stopCamera();
        Logger.info(url, chunksRef.current, "urls");
        if (drawAnimationRef.current) {
          cancelAnimationFrame(drawAnimationRef.current);
          drawAnimationRef.current = null;
        }

        canvasStreamRef.current?.getTracks().forEach((track) => track.stop());
        canvasStreamRef.current = null;
      };

      // Start recording
      recorder.start();
      setSeconds(0);
      setIsRecording(true);
    } catch (e) {}
  };

  const stopRecording = () => {
    if (!mediaRecorderRef.current || !isRecording) return;

    try {
      mediaRecorderRef.current.stop();
    } catch (e) {}

    setIsRecording(false);
  };

  // Recording timer effect
  useEffect(() => {
    if (!isRecording) return;

    timerRef.current = setInterval(() => {
      setSeconds((prev) => {
        if (prev >= MAX_DURATION - 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          stopRecording();
          return MAX_DURATION;
        }
        return prev + 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRecording]);

  const handleRecordAgain = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setRecordedBlob(null);
    setSeconds(0);
    startCamera(resolution);
  };

  const api = useApi();
  const { showAlert } = useAlert();

  const submitVideo = async () => {
    if (!recordedBlob) return;

    setUploadProgress(0);
    setUploadSuccess(false);

    try {
      const file = new File([recordedBlob], `video-${Date.now()}.webm`, {
        type: "video/webm",
      });

      api.setAuthToken(LocalStorage.getLocalData("access_token"));

      // 1. Get presigned URL
      const presignedRes = await api.get<{
        uploadURL: string;
        key: string;
        downloadURL: string;
      }>(`/create_upload_url?file_type=${mediaType}`);

      const { uploadURL, key, downloadURL } = presignedRes.data;

      // 2. Upload binary directly to S3
      await axios.put(uploadURL, file, {
        headers: {
          "Content-Type": file.type,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );

          setUploadProgress(Math.min(99, percentCompleted));
        },
      });

      // 3. Save metadata only
      const response = await api.post(`/gallery_upload`, {
        url: downloadURL,
        file_type: mediaType,
        program_id: program_id || selectedProgramId || "",
        subject_id: subject_id || "",
        topic_id: topic_id || "",
        video_id: video_id || "",
        program_name: program_name || selectedProgramName || "",
        video_name: video_name || "",
        subject_name: subject_name || "",
        topic_name: topic_name || "",
      });

      setUploadProgress(100);
      setUploadSuccess(true);

      setTimeout(async () => {
        await onSubmit(file);

        setPreviewUrl(null);
        setRecordedBlob(null);
        setSeconds(0);
        setUploadProgress(null);
        setUploadSuccess(false);
      }, 1200);
    } catch (error) {
      setUploadProgress(null);
      setUploadSuccess(false);
    }
  };

  const formatTime = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Keep refs up-to-date
  useEffect(() => {
    activeStreamRef.current = stream;
  }, [stream]);

  useEffect(() => {
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);

  // Clean up on unmount only
  useEffect(() => {
    return () => {
      if (activeStreamRef.current) {
        activeStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (drawAnimationRef.current) {
        cancelAnimationFrame(drawAnimationRef.current);
      }

      canvasStreamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  // Bind live stream to video element when ready
  useEffect(() => {
    if (videoRef.current && stream && !previewUrl) {
      videoRef.current.srcObject = stream;
      videoRef.current.muted = true; // Mute preview to avoid feedback loop
    }
  }, [stream, previewUrl]);

  // Render permission screen if not granted
  if (!permissionGranted) {
    return (
      <div className="permission_card">
        <div className="permission_icon_container">🎥</div>
        <h3>Camera & Microphone Access Required</h3>
        <p>
          To record Media with audio reviews, Jozuna needs permission to access
          your camera and microphone.
        </p>
        {permissionError && (
          <div
            style={{
              color: "#ef4444",
              fontSize: "13px",
              marginBottom: "16px",
              fontWeight: 500,
            }}
          >
            {permissionError}
          </div>
        )}
        <button
          className="primary_btn"
          onClick={() => startCamera(resolution)}
          disabled={loadingCamera}
        >
          {loadingCamera ? "Requesting Access..." : "Grant Camera & Mic Access"}
        </button>
      </div>
    );
  }

  return (
    <div className="capture_section">
      <div className="capture_card">
        <div className="capture_header">
          <h3>Record Video</h3>
          <p>
            Record a live demonstration of your hands-on projects (up to 30
            seconds). Compressed in WebM format.
          </p>
        </div>

        <div className="camera_container">
          {/* Active webcam stream */}
          {/* Hidden canvas used only for recording */}
          <canvas
            ref={canvasRef}
            width={1280}
            height={720}
            style={{ display: "none" }}
          />

          {!previewUrl ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="camera_preview"
            />
          ) : (
            <video
              src={previewUrl}
              controls
              className="captured_preview no-mirror"
            />
          )}
          {/* {!previewUrl && (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="camera_preview"
            />
          )}

          {previewUrl && (
            <video
              src={previewUrl}
              controls
              className="captured_preview no-mirror"
            />
          )} */}

          {/* Countdown timer overlay */}
          {prepCountdown !== null && (
            <div className="camera_overlay_countdown">
              {prepCountdown > 0 ? (
                <div className="countdown_number">{prepCountdown}</div>
              ) : (
                <div className="countdown_text">Action! 🎬</div>
              )}
            </div>
          )}

          {/* Recording HUD Overlay */}
          {!previewUrl && isRecording && (
            <div className="recording_hud">
              <div className="recording_badge">
                <span className="recording_dot"></span>
                RECORDING
              </div>
              <div className="record_hud_timer">
                {formatTime(seconds)} / {formatTime(MAX_DURATION)}
              </div>
            </div>
          )}

          {/* Bottom progress bar indicating remaining duration */}
          {!previewUrl && isRecording && (
            <div className="video_progress_container">
              <div
                className="video_progress_bar"
                style={{ width: `${(seconds / MAX_DURATION) * 100}%` }}
              ></div>
            </div>
          )}

          {/* Mic Volume Level Bar */}
          {!previewUrl && !isRecording && (
            <div className="audio_meter_container">
              <span>Mic Monitor:</span>
              <div className="audio_bars">
                <div
                  className={`audio_bar ${audioLevel > 5 ? "active" : ""}`}
                  style={{ height: `${Math.max(4, audioLevel * 0.12)}px` }}
                ></div>
                <div
                  className={`audio_bar ${audioLevel > 20 ? "active" : ""}`}
                  style={{ height: `${Math.max(4, audioLevel * 0.12)}px` }}
                ></div>
                <div
                  className={`audio_bar ${audioLevel > 40 ? "active" : ""}`}
                  style={{ height: `${Math.max(4, audioLevel * 0.12)}px` }}
                ></div>
                <div
                  className={`audio_bar ${audioLevel > 65 ? "active" : ""}`}
                  style={{ height: `${Math.max(4, audioLevel * 0.12)}px` }}
                ></div>
              </div>
            </div>
          )}

          {/* S3 Upload Progress overlay */}
          {uploadProgress !== null && (
            <div className="camera_overlay_uploading">
              {!uploadSuccess ? (
                <>
                  <div className="spinner"></div>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "16px",
                      letterSpacing: "0.2px",
                    }}
                  >
                    Uploading... ({uploadProgress}%)
                  </div>

                  {/* <div style={{ fontSize: "12px", opacity: 0.7 }}>
                    Generating pre-signed URL & storing video safely
                  </div> */}
                </>
              ) : (
                <>
                  <div className="upload_success_checkmark">✓</div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: "18px",
                      color: "#10b981",
                    }}
                  >
                    Uploaded Successfully!
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Action Controls Row */}
        <div className="capture_controls_row">
          {/* <div className="resolution_select_container">
            <label htmlFor="video-res">Resolution & Audio:</label>
            <select
              id="video-res"
              className="resolution_select"
              value={resolution}
              onChange={handleResolutionChange}
              disabled={isRecording || prepCountdown !== null || uploadProgress !== null || !!previewUrl}
            >
              <option value="VGA (640x480)">VGA (640x480)</option>
              <option value="HD (1280x720)">HD (1280x720)</option>
              <option value="FHD (1920x1080)">FHD (1920x1080)</option>
            </select>
          </div> */}

          <div className="capture_actions">
            {!previewUrl ? (
              !isRecording ? (
                <button
                  className="primary_btn"
                  onClick={startPreparationCountdown}
                  disabled={disabled || prepCountdown !== null || uploadProgress !== null}
                >
                  {prepCountdown !== null ? "Preparing..." : "Start Recording"}
                </button>
              ) : (
                <button className="danger_btn" onClick={stopRecording}>
                  Stop Recording
                </button>
              )
            ) : (
              <>
                <button
                  className="secondary_btn"
                  onClick={handleRecordAgain}
                  disabled={uploadProgress !== null}
                >
                  Record Again
                </button>
                <button
                  className="success_btn"
                  onClick={() => {
                    submitVideo();
                  }}
                  disabled={uploadProgress !== null}
                >
                  Submit to Gallery
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      {/* {isOverlayOpen && (
        <div
          onClick={() => setIsOverlayOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "500px",
              background: "#fff",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
            }}
          >
            <div style={{ marginBottom: "20px" }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: "22px",
                  fontWeight: 600,
                  color: "#0f172a",
                }}
              >
                Upload to Gallery
              </h3>

              <p
                style={{
                  marginTop: "8px",
                  color: "#64748b",
                  fontSize: "14px",
                }}
              >
                Enter a name for your Video before uploading it to the gallery.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#334155",
                }}
              >
                Video Name
              </label>

              <input
                type="text"
                value={fileName}
                onChange={(e) => {
                  setFileName(e.target.value);

                  if (e.target.value.trim()) {
                    setFileNameError("");
                  }
                }}
                placeholder="Enter Video name"
                style={{
                  width: "100%",
                  height: "48px",
                  border: fileNameError
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  borderRadius: "10px",
                  padding: "0 14px",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {fileNameError && (
                <div
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    marginTop: "6px",
                    fontWeight: 500,
                  }}
                >
                  {fileNameError}
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                marginTop: "24px",
              }}
            >
              <button
                onClick={() => {
                  setFileName("");
                  setIsOverlayOpen(false);
                }}
                style={{
                  minWidth: "100px",
                  height: "42px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  background: "#fff",
                  color: "#475569",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>

              <button
                disabled={!fileName.trim()}
                onClick={() => {
                  if (!fileName.trim()) {
                    setFileNameError("Image name is required");
                    return;
                  }

                  setFileNameError("");
                  setIsOverlayOpen(false);

                  submitVideo();
                }}
                style={{
                  minWidth: "120px",
                  height: "42px",
                  border: "none",
                  borderRadius: "8px",
                  background: !fileName.trim() ? "#cbd5e1" : "#173f8a",
                  color: "#fff",
                  cursor: !fileName.trim() ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  opacity: !fileName.trim() ? 0.8 : 1,
                }}
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default VideoRecorder;
